import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Alert } from "@/components/Alert";
import { Icon } from "@/components/Icon";
import { Switch } from "@/components/Switch";
import { date } from "@/utils/format";
import {
    getNextWorkflowStep,
    getPhaseStartDate,
    getStepRoleLabelKey,
    getWorkflowStepProgress,
    type WorkflowStep,
} from "@/utils/stepRole";

interface CompletePhaseModalProps {
    isOpen: boolean;
    onClose: () => void;
    step: WorkflowStep | null;
    steps: WorkflowStep[];
    developmentCreatedAt?: string | null;
    onConfirm: (shouldGenerateDoc: boolean, createTask: boolean) => void;
    isSubmitting: boolean;
    error?: string | null;
    requirementDocsCount?: number;
    existingTasksCount?: number;
    onGenerateDocument?: () => Promise<void>;
    isGeneratingDocument?: boolean;
    designDocs?: Array<{ id: number; name: string }>;
    designDocsCount?: number;
    onOpenDesignModal?: () => void;
    isReopen?: boolean;
}

interface DetailRowProps {
    label: string;
    value: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
        <span className="shrink-0 text-slate-500 dark:text-zinc-400">
            {label}
        </span>
        <span className="min-w-0 text-right font-medium text-slate-900 dark:text-zinc-100">
            {value}
        </span>
    </div>
);

/** Step markers evenly from first to last (1 at start, N at end). */
const getStepMarkerPercent = (stepNumber: number, total: number): number => {
    if (total <= 1) {
        return 100;
    }

    return Math.round(((stepNumber - 1) / (total - 1)) * 100);
};

export const CompletePhaseModal: React.FC<CompletePhaseModalProps> = ({
    isOpen,
    onClose,
    step,
    steps,
    developmentCreatedAt,
    onConfirm,
    isSubmitting,
    error = null,
    requirementDocsCount = 0,
    existingTasksCount = 0,
    onGenerateDocument,
    isGeneratingDocument = false,
    designDocs = [],
    designDocsCount = 0,
    onOpenDesignModal,
    isReopen = false,
}) => {
    const { t } = useTranslation("global");

    const isPhase1 =
        step?.tag === "request" ||
        step?.tag === "requirements" ||
        step?.step_key === "request" ||
        step?.step_key === "requirements" ||
        (steps[0] && steps[0].id === step?.id);
    const isPhase1MissingDoc =
        !isReopen && isPhase1 && requirementDocsCount === 0;

    const isPhase2 =
        step?.tag === "design" ||
        step?.step_key === "design" ||
        (steps[1] && steps[1].id === step?.id);
    const isPhase2MissingDesign =
        !isReopen && isPhase2 && designDocsCount === 0;

    const isPhaseFrontend =
        step?.tag === "frontend" || step?.step_key === "frontend";

    const [shouldGenerateDoc, setShouldGenerateDoc] = useState<boolean>(false);
    const [createTask, setCreateTask] = useState<boolean>(false);
    const [selectedDesignIds, setSelectedDesignIds] = useState<number[]>([]);

    useEffect(() => {
        if (isOpen) {
            setShouldGenerateDoc(false);
            setCreateTask(false);
            if (isPhaseFrontend) {
                // By default select all designs
                setSelectedDesignIds(designDocs.map((d) => d.id));
            } else {
                setSelectedDesignIds([]);
            }
        }
    }, [
        isOpen,
        requirementDocsCount,
        designDocsCount,
        isPhaseFrontend,
    ]);

    const handleSubmit = (): void => {
        if (isPhase1MissingDoc || isPhase2MissingDesign) {
            return;
        }

        if (isPhaseFrontend && !isReopen && selectedDesignIds.length > 0) {
            // Placeholder trigger for downloads
            selectedDesignIds.forEach((id) => {
                const doc = designDocs.find((d) => d.id === id);
                if (doc) {
                    const handleDownload = async () => {
                        try {
                            const blobHtml =
                                await developmentApi.getAttachmentBlob(
                                    development.id,
                                    id
                                );
                            const htmlText = await blobHtml.text();

                            const match = htmlText.match(
                                /<script type="text\/plain" id="tsx-source">([\s\S]*?)<\/script>/
                            );
                            let tsxContent = "";
                            if (match && match[1]) {
                                try {
                                    const bytes = Uint8Array.from(
                                        atob(match[1]),
                                        (c) => c.charCodeAt(0)
                                    );
                                    tsxContent = new TextDecoder().decode(
                                        bytes
                                    );
                                } catch (e) {
                                    tsxContent = match[1];
                                }
                            } else {
                                console.error(
                                    "Diseño sin TSX original:",
                                    doc.name
                                );
                                return;
                            }

                            const blob = new Blob([tsxContent], {
                                type: "text/plain",
                            });
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download =
                                doc.name.replace(/\.[^/.]+$/, "") + ".tsx";
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                        } catch (e) {
                            console.error("Error downloading TSX:", e);
                        }
                    };
                    handleDownload();
                }
            });
        }

        onConfirm(isPhase1 && !isReopen ? shouldGenerateDoc : false, createTask);
    };

    const phaseLabel = step
        ? step.tag
            ? t(`developments.detail.timeline.steps.${step.tag}`)
            : step.title
        : "";
    const stepKey = step?.tag || step?.step_key || "";
    const stepProgress = step ? getWorkflowStepProgress(steps, step) : null;
    const nextStep = step ? getNextWorkflowStep(steps, step) : null;
    const nextPhaseLabel = nextStep
        ? nextStep.tag
            ? t(`developments.detail.timeline.steps.${nextStep.tag}`)
            : nextStep.title
        : null;
    const startedAt = step
        ? getPhaseStartDate(steps, step, developmentCreatedAt)
        : null;

    const currentActiveProgress = getWorkflowStepProgress(steps);
    const currentActiveStep = currentActiveProgress
        ? steps[currentActiveProgress.current - 1]
        : null;
    const currentActivePhaseLabel = currentActiveStep
        ? currentActiveStep.tag
            ? t(`developments.detail.timeline.steps.${currentActiveStep.tag}`)
            : currentActiveStep.title
        : null;
    const targetStepIndex = nextStep
        ? steps.findIndex(
              (s) =>
                  (s.tag || s.step_key) === (nextStep.tag || nextStep.step_key)
          )
        : -1;
    const targetStepNumber =
        targetStepIndex >= 0
            ? targetStepIndex + 1
            : stepProgress
              ? Math.min(stepProgress.current + 1, stepProgress.total)
              : null;

    const actualProgressFrom =
        isReopen && currentActiveProgress
            ? currentActiveProgress.current
            : stepProgress?.current || 0;
    const actualProgressTo = isReopen
        ? stepProgress?.current || 0
        : targetStepNumber || 0;

    const progressFromPercent = stepProgress
        ? getStepMarkerPercent(actualProgressFrom, stepProgress.total)
        : 0;
    const progressToPercent = stepProgress
        ? getStepMarkerPercent(actualProgressTo, stepProgress.total)
        : 0;

    //'border border-emerald-400 bg-emerald-100 text-emerald-800 dark:border-emerald-600 dark:bg-emerald-950/70 dark:text-emerald-300'
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={
                isReopen
                    ? phaseLabel
                        ? t("developments.detail.step_reopen_btn") +
                          ": " +
                          phaseLabel
                        : t("developments.detail.step_reopen_btn")
                    : phaseLabel ||
                      t("developments.detail.phase_complete_confirm_question")
            }
            maxWidth="md"
            onSubmit={handleSubmit}
            submitLabel={
                isReopen
                    ? t("developments.detail.step_reopen_btn")
                    : t("developments.detail.phase_complete_confirm_submit")
            }
            submitVariant={isReopen ? "danger" : "primary"}
            submitIcon={
                <Icon
                    value={isReopen ? "undo" : "check"}
                    size={14}
                    strokeWidth={3}
                />
            }
            submitDisabled={
                isPhase1MissingDoc ||
                isPhase2MissingDesign ||
                isGeneratingDocument
            }
            isSubmitting={isSubmitting}
            submittingLabel={t("developments.detail.step_signing")}
        >
            {step && (
                <div className="space-y-5">
                    <p className="text-center text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                        {isReopen
                            ? t(
                                  "developments.detail.phase_reopen_confirm_question"
                              )
                            : t(
                                  "developments.detail.phase_complete_confirm_question"
                              )}
                    </p>

                    {stepProgress && (
                        <div className="rounded-xl bg-slate-50/90 px-4 py-3.5 dark:bg-zinc-900/50">
                            <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400">
                                    {t(
                                        "developments.detail.phase_complete_progress"
                                    )}
                                </span>
                                <div className="flex items-center gap-1.5 tabular-nums">
                                    <span className="text-sm font-semibold text-slate-500 dark:text-zinc-400">
                                        {progressFromPercent}%
                                    </span>
                                    <Icon
                                        value="arrowRight"
                                        size={14}
                                        strokeWidth={2.5}
                                        className="shrink-0 text-primary dark:text-primary-400"
                                    />
                                    <span className="phase-step-blink text-sm font-bold text-primary dark:text-primary-400">
                                        {progressToPercent}%
                                    </span>
                                </div>
                            </div>
                            <div className="relative px-3 pt-0.5">
                                <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-200/80 ring-1 ring-slate-200/40 dark:bg-white/15 dark:ring-white/10">
                                    {isReopen && (
                                        <div
                                            className="absolute inset-y-0 rounded-r-full bg-rose-500/80 transition-all duration-300 phase-bar-blink"
                                            style={{
                                                left: `${progressToPercent}%`,
                                                width: `${Math.max(0, progressFromPercent - progressToPercent)}%`,
                                            }}
                                        />
                                    )}
                                    <div
                                        className={`absolute inset-y-0 left-0 bg-primary transition-all duration-300 ${isReopen ? "rounded-l-full" : "rounded-full"}`}
                                        style={{
                                            width: `${progressToPercent}%`,
                                        }}
                                    />
                                </div>

                                <div className="relative mt-2 h-6">
                                    {Array.from(
                                        { length: stepProgress.total },
                                        (_, index) => {
                                            const stepNumber = index + 1;
                                            const isCompleted = isReopen
                                                ? stepNumber <
                                                  (currentActiveProgress?.current ||
                                                      0)
                                                : stepNumber <
                                                  stepProgress.current;
                                            const isCurrent = isReopen
                                                ? stepNumber ===
                                                  (currentActiveProgress?.current ||
                                                      0)
                                                : stepNumber ===
                                                  stepProgress.current;
                                            const isTarget = isReopen
                                                ? stepNumber ===
                                                  (stepProgress?.current || 0)
                                                : stepNumber ===
                                                  targetStepNumber;
                                            const isReverted =
                                                isReopen &&
                                                stepNumber >
                                                    (stepProgress?.current ||
                                                        0) &&
                                                stepNumber <=
                                                    (currentActiveProgress?.current ||
                                                        0);
                                            const markerLeft =
                                                getStepMarkerPercent(
                                                    stepNumber,
                                                    stepProgress.total
                                                );

                                            return (
                                                <span
                                                    key={stepNumber}
                                                    className={`absolute top-0 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full text-[11px] font-bold leading-none transition-colors ${
                                                        isTarget
                                                            ? "phase-step-blink bg-primary text-white shadow-xs"
                                                            : isReverted
                                                              ? "bg-rose-500 text-white shadow-xs"
                                                              : isCurrent ||
                                                                  isCompleted
                                                                ? "bg-primary text-white shadow-xs"
                                                                : "bg-slate-200/80 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400"
                                                    }`}
                                                    style={{
                                                        left: `${markerLeft}%`,
                                                    }}
                                                >
                                                    {stepNumber}
                                                </span>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-0.5 px-1">
                        {startedAt && (
                            <DetailRow
                                label={t(
                                    "developments.detail.phase_complete_started"
                                )}
                                value={date(startedAt, true)}
                            />
                        )}
                        <DetailRow
                            label={t(
                                "developments.detail.phase_complete_approval"
                            )}
                            value={t(getStepRoleLabelKey(stepKey))}
                        />
                        {!isReopen && (
                            <>
                                <DetailRow
                                    label={t(
                                        "developments.detail.phase_current"
                                    )}
                                    value={phaseLabel}
                                />
                                {nextPhaseLabel && (
                                    <DetailRow
                                        label={t(
                                            "developments.detail.phase_complete_next"
                                        )}
                                        value={nextPhaseLabel}
                                    />
                                )}
                            </>
                        )}
                        {isReopen && (
                            <>
                                <DetailRow
                                    label={t(
                                        "developments.detail.phase_current"
                                    )}
                                    value={currentActivePhaseLabel || ""}
                                />
                                <DetailRow
                                    label={t(
                                        "developments.detail.phase_reopen_target"
                                    )}
                                    value={phaseLabel}
                                />
                            </>
                        )}
                    </div>

                    {isPhase1 && (
                        <div className="space-y-3">
                        {isPhase1MissingDoc ? (
                                <div className="rounded-xl border border-amber-300 bg-amber-50/90 p-4 text-xs dark:border-amber-700/80 dark:bg-amber-950/40 space-y-3">
                                    <div className="flex justify-center items-center gap-2.5">
                                        <Icon
                                            value="alertTriangle"
                                            size={18}
                                            className="shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
                                        />
                                        <div className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                                            {t(
                                                "developments.detail.requirements_documents_phase1_missing_title"
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-center text-xs text-amber-900 dark:text-amber-200">
                                        {t(
                                            "developments.detail.requirements_documents_phase1_missing_desc"
                                        )}
                                    </div>

                                    {onGenerateDocument && (
                                        <div className="text-center">
                                            <button
                                                type="button"
                                                className="btn warning"
                                                disabled={isGeneratingDocument}
                                                onClick={() => {
                                                    void onGenerateDocument();
                                                }}
                                            >
                                                {isGeneratingDocument ? (
                                                    <>
                                                        <Icon
                                                            value="loader"
                                                            size={16}
                                                            className="animate-spin shrink-0"
                                                        />
                                                        <span>
                                                            {t(
                                                                "developments.detail.requirements_documents_generating"
                                                            )}
                                                        </span>
                                                    </>
                        ) : (
                                                    <>
                                                        <Icon
                                                            value="sparkles"
                                                            size={16}
                                                            className="shrink-0"
                                                        />
                                                        <span>
                                                            {t(
                                                                "developments.detail.requirements_documents_phase1_generate_btn"
                        )}

                                                        </span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                    {/*
                                    <p className="text-[11px] text-center font-medium text-amber-700 dark:text-amber-400">
                                        {t('developments.detail.requirements_documents_phase1_blocked')}
                                    </p>
                                    */}
                                </div>
                            ) : (
                                /*
                                <div className="rounded-xl border border-emerald-300 bg-emerald-50/80 p-3.5 text-xs dark:border-emerald-800 dark:bg-emerald-950/40 space-y-2">
                                    <div className="text-center">
                                        <Icon value="checkCircle" size={16} className="text-emerald-600 dark:text-emerald-400" />
                                        {t('developments.detail.requirements_documents_phase1_ready', {
                                            version: `v${requirementDocsCount}`,
                                        })}                                                                            
                                    </div>

                                    <label className="flex items-center gap-2 cursor-pointer text-slate-700 dark:text-zinc-300 pt-1">
                                        <input
                                            type="checkbox"
                                            checked={shouldGenerateDoc}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                setShouldGenerateDoc(e.target.checked);
                                            }}
                                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                                        />
                                        <span>{t('developments.detail.requirements_documents_phase1_regenerate_checkbox')}</span>
                                    </label>
                                </div>
                            */ <></>
                            )}
                        </div>
                    )}

                    {isPhase1 && !isReopen && (
                        <div
                            className="mt-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5"
                            onClick={() => setCreateTask((value) => !value)}
                        >
                            <div className="flex items-center justify-between gap-3 text-xs font-semibold text-slate-700 dark:text-zinc-300">
                                <div>
                                    {t(
                                        existingTasksCount > 0
                                            ? "developments.detail.create_another_automatic_task"
                                            : "developments.detail.create_automatic_task"
                                    )}
                                </div>
                                <Switch
                                    checked={createTask}
                                    onChange={setCreateTask}
                                    size="sm"
                                />
                            </div>
                        </div>
                    )}

                    {isPhase2 && isPhase2MissingDesign && (
                        <div className="rounded-xl border border-amber-300 bg-amber-50/90 p-4 text-xs dark:border-amber-700/80 dark:bg-amber-950/40 space-y-3">
                            <div className="flex justify-center items-center gap-2.5">
                                <Icon
                                    value="alertTriangle"
                                    size={18}
                                    className="shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
                                />
                                <div className="font-bold text-amber-900 dark:text-amber-200 text-sm">
                                    {t(
                                        "developments.detail.design_documents_phase2_missing_title"
                                    )}
                                </div>
                            </div>
                            <div className="text-center text-xs text-amber-900 dark:text-amber-200">
                                {t(
                                    "developments.detail.design_documents_phase2_missing_desc"
                                )}
                            </div>

                            {onOpenDesignModal && (
                                <div className="text-center">
                                    <button
                                        type="button"
                                        className="btn warning inverted"
                                        onClick={() => {
                                            onClose();
                                            onOpenDesignModal();
                                        }}
                                    >
                                        <Icon
                                            value="palette"
                                            size={16}
                                            className="shrink-0"
                                        />
                                        <span>
                                            {t(
                                                "developments.detail.generate_design_button"
                                            )}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {isPhaseFrontend && !isReopen && designDocs.length > 0 && (
                        <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 dark:border-blue-900/30 dark:bg-blue-900/10 mb-4 mt-6">
                            <h4 className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2">
                                <Icon value="code" size={16} />
                                {t(
                                    "developments.detail.phase_complete_download_react_title"
                                )}
                            </h4>
                            <p className="mb-3 text-xs text-blue-700 dark:text-blue-300">
                                {t(
                                    "developments.detail.phase_complete_download_react_desc"
                                )}
                            </p>
                            <div className="space-y-2 max-h-[150px] overflow-y-auto">
                                {designDocs.map((doc) => (
                                    <label
                                        key={doc.id}
                                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-blue-100/50 dark:hover:bg-blue-900/20 p-2 rounded-lg transition-colors"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedDesignIds.includes(
                                                doc.id
                                            )}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedDesignIds([
                                                        ...selectedDesignIds,
                                                        doc.id,
                                                    ]);
                                                } else {
                                                    setSelectedDesignIds(
                                                        selectedDesignIds.filter(
                                                            (id) =>
                                                                id !== doc.id
                                                        )
                                                    );
                                                }
                                            }}
                                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <Icon
                                            value="image"
                                            size={14}
                                            className="text-slate-400"
                                        />
                                        <span className="truncate text-slate-700 dark:text-slate-300 flex-1">
                                            {doc.name}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
};
